import { useRef, useState } from "react";
import { Loader2, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import {
  DISPARO_MEDIA_ACCEPT,
  isShareLinkNotDirectFile,
  uploadDisparoMedia,
  type DisparoMediaType,
} from "@/lib/disparo-media-upload";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DISPARO_MESSAGE_TYPE_LABELS,
  type DisparoMessagePayload,
  type DisparoMessageType,
} from "@/lib/disparos-types";
import { isDisparoInteractiveType } from "@/lib/disparos-interactive";

const MEDIA_TYPES: DisparoMessageType[] = [
  "image",
  "video",
  "audio",
  "document",
];

const SIMPLE_MESSAGE_TYPES: DisparoMessageType[] = [
  "text",
  "image",
  "video",
  "audio",
  "document",
  "list",
  "button",
  "poll",
  "carousel",
];

interface DisparoSimpleFormProps {
  workspaceId: string;
  campaignName: string;
  onCampaignNameChange: (v: string) => void;
  message: DisparoMessagePayload;
  onMessageChange: (m: DisparoMessagePayload) => void;
  delayMin: number;
  delayMax: number;
  onDelayMinChange: (v: number) => void;
  onDelayMaxChange: (v: number) => void;
  scheduledAt: string;
  onScheduledAtChange: (v: string) => void;
}

export function DisparoSimpleForm({
  workspaceId,
  campaignName,
  onCampaignNameChange,
  message,
  onMessageChange,
  delayMin,
  delayMax,
  onDelayMinChange,
  onDelayMaxChange,
  scheduledAt,
  onScheduledAtChange,
}: DisparoSimpleFormProps) {
  const isMedia = MEDIA_TYPES.includes(message.type);
  const isInteractive = isDisparoInteractiveType(message.type);
  const extra = message.extra ?? {};

  const updateExtra = (patch: Record<string, unknown>) => {
    onMessageChange({ ...message, extra: { ...extra, ...patch } });
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState<string | null>(null);

  const handleFileSelected = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadDisparoMedia({
        workspaceId,
        file,
        mediaType: message.type as DisparoMediaType,
      });
      onMessageChange({ ...message, file: url });
      setUploadedName(file.name);
      toast.success("Arquivo enviado");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao enviar o arquivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="disparo-name">Nome da campanha</Label>
        <Input
          id="disparo-name"
          placeholder="Ex: Promoção março"
          value={campaignName}
          onChange={(e) => onCampaignNameChange(e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Tipo de mensagem</Label>
        <Select
          value={message.type}
          onValueChange={(v) => onMessageChange({ ...message, type: v as DisparoMessageType })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SIMPLE_MESSAGE_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {DISPARO_MESSAGE_TYPE_LABELS[t]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {message.type === "text" ? (
        <div className="space-y-1.5">
          <Label htmlFor="disparo-text">Mensagem</Label>
          <Textarea
            id="disparo-text"
            placeholder="Digite a mensagem..."
            rows={5}
            value={message.text || ""}
            onChange={(e) => onMessageChange({ ...message, text: e.target.value })}
          />
        </div>
      ) : isMedia ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="disparo-file">Arquivo</Label>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={DISPARO_MEDIA_ACCEPT[message.type as DisparoMediaType]}
              onChange={(e) => handleFileSelected(e.target.files?.[0])}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Paperclip className="h-4 w-4" />
                )}
                {uploading ? "Enviando..." : "Escolher arquivo"}
              </Button>
              {message.file ? (
                <span className="flex items-center gap-1 text-xs text-muted-foreground truncate max-w-[60%]">
                  {uploadedName || message.file}
                  <button
                    type="button"
                    aria-label="Remover arquivo"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      onMessageChange({ ...message, file: "" });
                      setUploadedName(null);
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ) : null}
            </div>
            <Input
              id="disparo-file"
              placeholder="ou cole um link direto do arquivo (https://...)"
              value={message.file || ""}
              onChange={(e) => onMessageChange({ ...message, file: e.target.value })}
            />
            {message.file && isShareLinkNotDirectFile(message.file) ? (
              <p className="text-xs text-destructive">
                Links do Google Drive, Docs, Dropbox ou OneDrive não funcionam: o WhatsApp não
                consegue baixar o arquivo. Envie a imagem pelo botão acima.
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="disparo-caption">Legenda (opcional)</Label>
            <Textarea
              id="disparo-caption"
              rows={3}
              value={message.caption || message.text || ""}
              onChange={(e) =>
                onMessageChange({ ...message, caption: e.target.value, text: e.target.value })
              }
            />
          </div>
        </>
      ) : isInteractive ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="disparo-interactive-text">
              {message.type === "poll" ? "Pergunta da enquete" : "Texto principal"}
            </Label>
            <Textarea
              id="disparo-interactive-text"
              rows={3}
              value={message.text || ""}
              onChange={(e) => onMessageChange({ ...message, text: e.target.value })}
            />
          </div>
          {message.type === "list" && (
            <div className="space-y-1.5">
              <Label htmlFor="disparo-list-button">Texto do botão do menu</Label>
              <Input
                id="disparo-list-button"
                value={String(extra.listButton ?? "Ver opções")}
                onChange={(e) => updateExtra({ listButton: e.target.value })}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="disparo-choices">
              {message.type === "button" || message.type === "list"
                ? "Opções (uma por linha: título|id|descrição opcional)"
                : message.type === "poll"
                  ? "Opções da enquete (uma por linha)"
                  : "Cartões do carrossel (JSON)"}
            </Label>
            {message.type === "carousel" ? (
              <Textarea
                id="disparo-choices"
                rows={6}
                placeholder='[{"text":"Cartão 1","image":"https://...","buttons":[{"id":"b1","text":"Saiba mais"}]}]'
                value={
                  extra.carousel
                    ? JSON.stringify(extra.carousel, null, 2)
                    : message.text || ""
                }
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    updateExtra({ carousel: parsed });
                  } catch {
                    onMessageChange({ ...message, text: e.target.value });
                  }
                }}
              />
            ) : (
              <Textarea
                id="disparo-choices"
                rows={5}
                placeholder={
                  message.type === "button"
                    ? "Suporte Técnico|suporte\nFazer Pedido|pedido"
                    : message.type === "list"
                      ? "[Seção]\nItem A|item_a|Descrição opcional"
                      : "Opção 1\nOpção 2\nOpção 3"
                }
                value={
                  Array.isArray(extra.choices)
                    ? (extra.choices as string[]).join("\n")
                    : ""
                }
                onChange={(e) => {
                  const choices = e.target.value
                    .split("\n")
                    .map((line) => line.trim())
                    .filter(Boolean);
                  updateExtra({ choices });
                }}
              />
            )}
          </div>
          {(message.type === "button" || message.type === "list") && (
            <div className="space-y-1.5">
              <Label htmlFor="disparo-footer">Rodapé (opcional)</Label>
              <Input
                id="disparo-footer"
                value={String(extra.footerText ?? "")}
                onChange={(e) => updateExtra({ footerText: e.target.value })}
              />
            </div>
          )}
        </>
      ) : null}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="delay-min">Delay mínimo (s)</Label>
          <Input
            id="delay-min"
            type="number"
            min={0}
            value={delayMin}
            onChange={(e) => onDelayMinChange(Number(e.target.value) || 0)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="delay-max">Delay máximo (s)</Label>
          <Input
            id="delay-max"
            type="number"
            min={0}
            value={delayMax}
            onChange={(e) => onDelayMaxChange(Number(e.target.value) || 0)}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="scheduled-at">Agendar para (opcional)</Label>
        <Input
          id="scheduled-at"
          type="datetime-local"
          value={scheduledAt}
          onChange={(e) => onScheduledAtChange(e.target.value)}
        />
      </div>
    </div>
  );
}
